import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import DateBox from "@/components/ui/DateBox";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import { InputBox } from "@/components/ui/InputBox";
import { CheckBox } from "@/components/ui/CheckBox";
import SelectBox from "@/components/ui/SelectBox";
import Table, { TableColumn } from "@/components/ui/Table";
import { Footer } from "@/components/layout/Footer";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { formatNumber } from "@/lib/utils";
import {
  useSalesTransactionClients,
  useSalesTransactionDetails,
  useSalesTransactionTotals,
  fetchAllSalesTransactionDetails,
} from "@/hooks/settlement/se_salesTransactionByClient/useSalesTransactionByClient";
import { SalesTransactionClient, SalesTransactionDetail } from "@/interface/settlement/salesTransactionByClient";
import { useCodeListByCompanyCode } from "@/hooks/standard/st_code/useCode";
import { Code } from "@/interface/standard/st_code/index";
import { getToday } from "@/lib/date";
import { createTransactionPdfBlob, TransactionItem } from "@/lib/transactionPdf";
import { toast } from "sonner";
import { useCompanyInfo } from "@/hooks/setting/se_company/useCompanySetting";
import { SeCompany } from "@/interface/setting/se_company";
import ModalLayout from "@/components/layout/ModalLayout";

export default function SalesTransactionByClientPage() {
  const today = getToday();

  // 회사 정보 조회 (공급자 정보용) - se_company 테이블에서 전체 목록 가져옴
  const { data: companyInfoList } = useCompanyInfo({ page: 0, size: 100 });

  // 회사 선택 모달 상태
  const [companySelectOpen, setCompanySelectOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<SeCompany | null>(null);

  // PDF 미리보기 모달 상태
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [currentPdfFileName, setCurrentPdfFileName] = useState("");

  // 좌우 패널 리사이즈 상태
  const [leftPanelWidth, setLeftPanelWidth] = useState(320);
  const isResizing = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 상단 검색 영역 (우측 패널용)
  const [company, setCompany] = useState("");
  const [locationSearchType, setLocationSearchType] = useState("inCompany");
  const [locationSearchValue, setLocationSearchValue] = useState("");
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [dateBtnValue, setDateBtnValue] = useState("today");

  // 좌측 패널 전용 검색 (매출일자 기준으로 필터링)
  const [leftSearchType, setLeftSearchType] = useState("companyName");
  const [leftSearchValue, setLeftSearchValue] = useState("");
  // 정확히 일치 체크 (모든 검색에 적용)
  const [exactMatch, setExactMatch] = useState(false);
  const [leftSearchParams, setLeftSearchParams] = useState({
    searchType: "",
    search: "",
    exactMatch: false,
  });

  // 우측 패널용 검색 파라미터 (날짜 변경 시 실시간 반영)
  const transactionParams = useMemo(
    () => ({
      startDate,
      endDate,
      company,
      locationSearchType,
      locationSearch: locationSearchValue,
      locationExactMatch: exactMatch,
    }),
    [startDate, endDate, company, locationSearchType, locationSearchValue, exactMatch]
  );

  // 선택된 거래처
  const [selectedClientCode, setSelectedClientCode] = useState<string>("");

  // 좌측 패널 페이지네이션 상태
  const [leftCurrentPage, setLeftCurrentPage] = useState(0);
  const [leftAllData, setLeftAllData] = useState<(SalesTransactionClient & { no: number })[]>([]);
  const [leftLoadingMore, setLeftLoadingMore] = useState(false);
  const [leftTotalElements, setLeftTotalElements] = useState(0);

  // 우측 패널 페이지네이션 상태
  const [rightCurrentPage, setRightCurrentPage] = useState(0);
  const [rightAllData, setRightAllData] = useState<(SalesTransactionDetail & { no: number })[]>([]);
  const [rightLoadingMore, setRightLoadingMore] = useState(false);
  const [rightTotalElements, setRightTotalElements] = useState(0);

  // 회사 목록
  const { data: companyCodeList = [] } = useCodeListByCompanyCode();

  // 거래처 목록 API (좌측 패널 - Page 응답, 매출일자 기준)
  const { data: clientsData, isLoading: clientsLoading } = useSalesTransactionClients({
    startDate,
    endDate,
    company,
    ...leftSearchParams,
    page: leftCurrentPage,
    size: 20,
  });

  // 거래 내역 API (우측 패널 - 거래처 선택 시에만 조회)
  const { data: transactionsData, isLoading: transactionsLoading } = useSalesTransactionDetails(selectedClientCode, {
    ...transactionParams,
    page: rightCurrentPage,
    size: 20,
  });

  // 거래 합계 API (Footer용 - 전체 검색 결과 기준)
  const { data: totalsData } = useSalesTransactionTotals(selectedClientCode, transactionParams);

  // 좌측 패널: 날짜/회사 변경 시 좌측 데이터만 초기화 (매출처 선택은 유지)
  useEffect(() => {
    setLeftCurrentPage(0);
    setLeftAllData([]);
    setLeftTotalElements(0);
  }, [startDate, endDate, company]);

  // 좌측 패널: 데이터 누적 로직
  useEffect(() => {
    if (clientsData?.content) {
      setLeftAllData(prev => {
        // 첫 페이지면 초기화
        if (leftCurrentPage === 0) {
          setLeftTotalElements(clientsData.totalElements || 0);
          const newAllData = clientsData.content.map((item, index) => ({
            no: index + 1,
            ...item,
          }));
          // 선택된 매출처가 새 기간에 없으면 선택 해제
          if (selectedClientCode) {
            const found = newAllData.some(item => item.companyCode === selectedClientCode);
            if (!found) {
              setSelectedClientCode("");
            }
          }
          return newAllData;
        }
        // 다음 페이지면 중복 제거 후 추가
        const newData = clientsData.content.filter(
          newItem => !prev.some(existingItem => existingItem.companyCode === newItem.companyCode)
        );
        return [
          ...prev,
          ...newData.map((item, index) => ({
            no: prev.length + index + 1,
            ...item,
          })),
        ];
      });
    }
  }, [clientsData, leftCurrentPage, selectedClientCode]);

  // 좌측 패널: 로딩 상태 업데이트
  useEffect(() => {
    if (clientsData) {
      setLeftLoadingMore(false);
    }
  }, [clientsData]);

  // 좌측 패널: hasMore 계산
  const leftHasMore = useMemo(() => {
    if (!clientsData) return false;
    return !clientsData.last && leftAllData.length < leftTotalElements;
  }, [clientsData, leftAllData.length, leftTotalElements]);

  // 좌측 패널: 무한 스크롤 콜백
  const handleLeftLoadMore = useCallback(async () => {
    if (!leftHasMore || leftLoadingMore) return;
    setLeftLoadingMore(true);
    setLeftCurrentPage(prev => prev + 1);
  }, [leftHasMore, leftLoadingMore]);

  // 우측 패널용 첫 마운트 여부 추적
  const isFirstMountRight = useRef(true);

  // 우측 패널: 검색 조건 또는 거래처 변경 시 데이터 초기화 (첫 마운트 제외)
  useEffect(() => {
    if (isFirstMountRight.current) {
      isFirstMountRight.current = false;
      return;
    }
    setRightCurrentPage(0);
    setRightAllData([]);
    setRightTotalElements(0);
    setRightLoadingMore(false);
  }, [transactionParams, selectedClientCode]);

  // 우측 패널: 데이터 누적 로직
  useEffect(() => {
    if (transactionsData?.content) {
      setRightAllData(prev => {
        // 첫 페이지면 초기화
        if (rightCurrentPage === 0) {
          setRightTotalElements(transactionsData.totalElements || 0);
          return transactionsData.content.map((item, index) => ({
            no: index + 1,
            ...item,
          }));
        }
        // 다음 페이지면 중복 제거 후 추가
        const newData = transactionsData.content.filter(
          newItem => !prev.some(existingItem => existingItem.saleSlipCode === newItem.saleSlipCode)
        );
        return [
          ...prev,
          ...newData.map((item, index) => ({
            no: prev.length + index + 1,
            ...item,
          })),
        ];
      });
    }
  }, [transactionsData, rightCurrentPage]);

  // 우측 패널: 로딩 상태 업데이트
  useEffect(() => {
    if (transactionsData) {
      setRightLoadingMore(false);
    }
  }, [transactionsData]);

  // 우측 패널: hasMore 계산
  const rightHasMore = useMemo(() => {
    if (!transactionsData) return false;
    return !transactionsData.last && rightAllData.length < rightTotalElements;
  }, [transactionsData, rightAllData.length, rightTotalElements]);

  // 우측 패널: 무한 스크롤 콜백
  const handleRightLoadMore = useCallback(async () => {
    if (!rightHasMore || rightLoadingMore) return;
    setRightLoadingMore(true);
    setRightCurrentPage(prev => prev + 1);
  }, [rightHasMore, rightLoadingMore]);

  // 회사 옵션
  const companyOptions = useMemo(
    () => [
      { label: "전체", value: "" },
      ...companyCodeList.map((item: Code) => ({
        value: item.code || item.codeId?.toString() || "",
        label: item.name || "",
      })),
    ],
    [companyCodeList]
  );

  // 좌측 패널 전용 검색 옵션
  const leftSearchTypeOptions = [
    { label: "매출처", value: "companyName" },
    { label: "업체코드", value: "companyCode" },
  ];

  // 좌측 패널 검색 핸들러
  const handleLeftSearch = () => {
    setLeftSearchParams({
      searchType: leftSearchType,
      search: leftSearchValue,
      exactMatch,
    });
    // 데이터 초기화
    setLeftCurrentPage(0);
    setLeftAllData([]);
    setLeftTotalElements(0);
    setSelectedClientCode("");
  };

  const locationSearchTypeOptions = [
    { label: "상차지", value: "inCompany" },
    { label: "하차지", value: "outCompany" },
    { label: "품목명", value: "itemName" },
    { label: "비고", value: "remark" },
  ];

  const handleReset = () => {
    const resetDate = getToday();
    setCompany("");
    setLocationSearchType("inCompany");
    setLocationSearchValue("");
    setStartDate(resetDate);
    setEndDate(resetDate);
    setDateBtnValue("today");
    setSelectedClientCode("");
    // 좌측 패널 검색 초기화
    setLeftSearchType("companyName");
    setLeftSearchValue("");
    setExactMatch(false);
    setLeftSearchParams({
      searchType: "",
      search: "",
      exactMatch: false,
    });
    // 좌측 패널 데이터 초기화
    setLeftCurrentPage(0);
    setLeftAllData([]);
    setLeftTotalElements(0);
  };

  // 좌측 거래처 목록 컬럼 (매출처는 flex로 나머지 공간 차지)
  // group을 추가하여 우측 테이블과 헤더 높이를 맞춤
  const clientColumns: TableColumn[] = [
    { key: "no", header: "번호", bodyAlign: "center", width: "50px", minWidth: "50px", maxWidth: "50px" },
    {
      key: "companyName",
      header: "매출처",
      bodyAlign: "left",
      group: "거래처 정보",
      render: (v: string) => (
        <span className="block truncate" title={v}>
          {v}
        </span>
      ),
    },
    {
      key: "totalCount",
      header: "건수",
      bodyAlign: "center",
      width: "60px",
      minWidth: "60px",
      maxWidth: "60px",
      group: "거래처 정보",
      render: (v: number) => formatNumber(v ?? 0),
    },
  ];

  // 우측 거래 내역 컬럼
  const transactionColumns: TableColumn[] = [
    { key: "no", header: "번호", bodyAlign: "center", width: "60px", minWidth: "60px", maxWidth: "60px" },
    { key: "saleDate", header: "매출 일자", bodyAlign: "center", width: "110px", minWidth: "110px", maxWidth: "110px" },
    {
      key: "inCompanyName",
      header: "상차지",
      bodyAlign: "left",
      width: "100px",
      minWidth: "100px",
      maxWidth: "100px",
      group: "업체",
    },
    {
      key: "outCompanyName",
      header: "하차지",
      bodyAlign: "left",
      width: "100px",
      minWidth: "100px",
      maxWidth: "100px",
      group: "업체",
    },
    { key: "itemName", header: "품목명", bodyAlign: "left", width: "150px", minWidth: "150px", maxWidth: "150px" },
    {
      key: "qty",
      header: "수량",
      bodyAlign: "right",
      width: "80px",
      minWidth: "80px",
      maxWidth: "80px",
      render: (v: number) => (v ?? 0).toFixed(2),
    },
    {
      key: "unitPrice",
      header: "단가",
      bodyAlign: "right",
      width: "80px",
      minWidth: "80px",
      maxWidth: "80px",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "supplyAmt",
      header: "공급가액",
      bodyAlign: "right",
      width: "100px",
      minWidth: "100px",
      maxWidth: "100px",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "vatAmt",
      header: "부가세액",
      bodyAlign: "right",
      width: "100px",
      minWidth: "100px",
      maxWidth: "100px",
      render: (v: number) => formatNumber(v ?? 0),
    },
    {
      key: "totalAmt",
      header: "합계",
      bodyAlign: "right",
      width: "100px",
      minWidth: "100px",
      maxWidth: "100px",
      render: (v: number) => formatNumber(v ?? 0),
    },
    { key: "remark", header: "비고", bodyAlign: "left", width: "120px", minWidth: "120px", maxWidth: "120px" },
  ];

  // Footer 합계 (API에서 전체 검색 결과 기준으로 조회)
  const footerGrid = useMemo(() => {
    const totalQty = totalsData?.totalQty ?? 0;
    const totalSupply = totalsData?.totalSupplyAmt ?? 0;
    const totalVat = totalsData?.totalVatAmt ?? 0;
    const totalAmt = totalsData?.totalAmt ?? 0;

    return [
      [
        { label: "수량", value: totalQty.toFixed(2) },
        { label: "공급가액", value: formatNumber(totalSupply) },
        { label: "부가세액", value: formatNumber(totalVat) },
        { label: "합계", value: formatNumber(totalAmt) },
      ],
    ];
  }, [totalsData]);

  // 거래처 행 클릭 핸들러
  const handleClientRowClick = (row: SalesTransactionClient & { no: number }) => {
    setSelectedClientCode(row.companyCode);
  };

  // 선택된 거래처 이름 가져오기
  const selectedClientName = useMemo(() => {
    const client = leftAllData.find(item => item.companyCode === selectedClientCode);
    return client?.companyName || "";
  }, [leftAllData, selectedClientCode]);

  // 거래명세표 버튼 클릭 - 회사 선택 모달 열기
  const handleTransactionPdfClick = () => {
    if (!selectedClientCode || rightAllData.length === 0) {
      toast.error("거래처를 선택하고 거래 내역이 있어야 합니다.");
      return;
    }
    // 회사 목록이 1개면 바로 선택, 여러 개면 모달 열기
    if (companyInfoList && companyInfoList.length === 1) {
      handleSupplierSelect(companyInfoList[0]);
    } else {
      setCompanySelectOpen(true);
    }
  };

  // 회사 선택 후 PDF 미리보기 생성
  const handleSupplierSelect = async (supplierData: SeCompany) => {
    setSelectedSupplier(supplierData);
    setCompanySelectOpen(false);

    try {
      // 전체 거래 내역 조회 (페이지네이션 없이)
      const allTransactions = await fetchAllSalesTransactionDetails(selectedClientCode, {
        startDate,
        endDate,
        company,
        locationSearchType,
        locationSearch: locationSearchValue,
        locationExactMatch: exactMatch,
      });

      // 거래 내역을 PDF용 데이터로 변환
      const items: TransactionItem[] = allTransactions.map(item => ({
        date: item.saleDate ? item.saleDate.substring(5).replace("-", "/") : "", // YYYY-MM-DD -> MM/DD
        inCompany: item.inCompanyName || "",
        outCompany: item.outCompanyName || "",
        itemName: item.itemName || "",
        qty: item.qty || 0,
        unitPrice: item.unitPrice || 0,
        amount: item.supplyAmt || 0,
        vatAmount: item.vatAmt || 0,
        remark: item.remark || "",
      }));

      // 공급자 정보 (선택한 회사 정보)
      const supplier = {
        businessNumber: supplierData?.bizNo || "",
        companyName: supplierData?.companyName || "",
        representativeName: supplierData?.ceoName || "",
        address: supplierData?.address || "",
        businessType: supplierData?.bizType || "",
        businessItem: supplierData?.bizItem || "",
      };

      // PDF Blob 생성 및 미리보기 열기
      const blob = createTransactionPdfBlob({
        clientName: selectedClientName,
        supplier,
        items,
      });
      const url = URL.createObjectURL(blob);
      setPdfBlobUrl(url);
      setCurrentPdfFileName(`거래명세표_${selectedClientName}`);
      setPreviewOpen(true);
    } catch (error) {
      console.error("전체 거래 내역 조회 실패:", error);
      toast.error("거래 내역을 불러오는데 실패했습니다.");
    }
  };

  // PDF 다운로드
  const handlePdfDownload = () => {
    if (!pdfBlobUrl) return;
    const today = new Date();
    const dateStr = `${today.getFullYear()}${String(today.getMonth() + 1).padStart(2, "0")}${String(today.getDate()).padStart(2, "0")}`;
    const link = document.createElement("a");
    link.href = pdfBlobUrl;
    link.download = `${currentPdfFileName}_${dateStr}.pdf`;
    link.click();
  };

  // PDF 인쇄
  const handlePdfPrint = () => {
    if (!pdfBlobUrl) return;
    const printWindow = window.open(pdfBlobUrl, "_blank");
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  };

  // 미리보기 모달 닫기
  const handlePreviewClose = () => {
    setPreviewOpen(false);
    if (pdfBlobUrl) {
      URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
    }
  };

  // 패널 리사이즈 핸들러
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const startX = e.clientX;
    const startWidth = leftPanelWidth;

    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = e.clientX - startX;
      const newWidth = Math.max(200, Math.min(600, startWidth + delta));
      setLeftPanelWidth(newWidth);
    };

    const handleMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, [leftPanelWidth]);

  return (
    <div className="flex flex-col h-full">
      {/* 검색영역 */}
      <SearchBoxLayout>
        <div className="flex items-end">
          {/* 좌측 패널 전용 검색 (매출처/업체코드) */}
          <SelectBox
            label="구분"
            className="w-28"
            value={leftSearchType}
            onChange={setLeftSearchType}
            options={leftSearchTypeOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-40"
            value={leftSearchValue}
            onChange={e => setLeftSearchValue(e.target.value)}
            placeholder="입력"
            onKeyDown={e => {
              if (e.key === "Enter") handleLeftSearch();
            }}
          />
          <div className="mr-1" />
          <Button variant="search" onClick={handleLeftSearch}>
            검색
          </Button>
          <div className="mr-3" />

          {/* 우측 패널용 검색 */}
          <SelectBox label="회사" className="w-40" value={company} onChange={setCompany} options={companyOptions} />
          <div className="mr-3" />
          <SelectBox
            label="구분"
            className="w-28"
            value={locationSearchType}
            onChange={setLocationSearchType}
            options={locationSearchTypeOptions}
          />
          <div className="mr-1" />
          <InputBox
            label=""
            className="w-40"
            value={locationSearchValue}
            onChange={e => setLocationSearchValue(e.target.value)}
            placeholder="입력"
          />
          <div className="mr-3" />

          <DateBox
            label="매출일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-2" />
          <DateSearchBtn
            mode="range"
            value={dateBtnValue}
            onChangeValue={setDateBtnValue}
            currentRange={{ startDate, endDate }}
            onDateSelect={({ startDate, endDate }) => {
              setStartDate(startDate);
              setEndDate(endDate);
            }}
          />

          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="일치"
              labelPosition="right"
              checked={exactMatch}
              onChange={e => setExactMatch(e.target.checked)}
            />
            <Button variant="reset" onClick={handleReset}>
              초기화
            </Button>
          </div>
        </div>
      </SearchBoxLayout>

      {/* 상단 버튼 */}
      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-end mb-2">
          <div className="flex gap-2">
            <Button
              variant="etc"
              onClick={handleTransactionPdfClick}
              disabled={!selectedClientCode || rightAllData.length === 0}
            >
              거래명세표
            </Button>
            <PrintButton
              data={rightAllData}
              columns={transactionColumns}
              title="거래처별매출액명세서"
              skipPermissionCheck
            />
            <ExcelButton
              data={rightAllData}
              columns={transactionColumns}
              fileName="거래처별매출액명세서"
              skipPermissionCheck
            />
          </div>
        </div>

        {/* 본문 - 좌우 분할 */}
        <div ref={containerRef} className="flex-1 min-h-0 flex">
          {/* 좌측: 거래처 목록 */}
          <div style={{ width: leftPanelWidth, minWidth: 200, maxWidth: 600 }} className="flex-shrink-0">
            <Table
              className="h-full"
              columns={clientColumns}
              data={leftAllData}
              showRowNumber={false}
              maxHeight="100%"
              onRowClick={handleClientRowClick}
              highlightOnClick
              selectedRows={
                selectedClientCode
                  ? [leftAllData.findIndex(item => item.companyCode === selectedClientCode)].filter(i => i >= 0)
                  : []
              }
              loading={clientsLoading && leftAllData.length === 0}
              emptyMessage="거래처를 검색하세요"
              infiniteScroll={true}
              initialLoadCount={20}
              loadMoreCount={20}
              hasMore={leftHasMore}
              onLoadMore={handleLeftLoadMore}
              loadingMore={leftLoadingMore}
              enablePagination={true}
              pageSize={20}
              onPageChange={setLeftCurrentPage}
              currentPage={leftCurrentPage}
            />
          </div>

          {/* 리사이즈 핸들 */}
          <div
            onMouseDown={handleMouseDown}
            className="w-2 flex-shrink-0 cursor-col-resize hover:bg-blue-200 active:bg-blue-300 transition-colors flex items-center justify-center group"
          >
            <div className="w-0.5 h-8 bg-gray-300 group-hover:bg-blue-400 rounded-full" />
          </div>

          {/* 우측: 거래 내역 */}
          <div className="flex-1 min-w-0">
            <Table
              className="h-full"
              columns={transactionColumns}
              data={rightAllData}
              showRowNumber={false}
              resizable
              maxHeight="100%"
              loading={transactionsLoading && rightAllData.length === 0}
              emptyMessage="거래처를 선택하세요"
              infiniteScroll={true}
              initialLoadCount={20}
              loadMoreCount={20}
              hasMore={rightHasMore}
              onLoadMore={handleRightLoadMore}
              loadingMore={rightLoadingMore}
              enablePagination={true}
              pageSize={20}
              onPageChange={setRightCurrentPage}
              currentPage={rightCurrentPage}
            />
          </div>
        </div>
      </main>

      {/* 푸터 총계 */}
      <footer className="flex-shrink-0 p-4 bg-gray-50 border-t">
        <Footer grid={footerGrid} />
      </footer>

      {/* 회사 선택 모달 */}
      <ModalLayout
        isOpen={companySelectOpen}
        onClose={() => setCompanySelectOpen(false)}
        title="공급자 선택"
        maxWidth="max-w-md"
      >
        <div className="flex flex-col gap-2">
          {companyInfoList && companyInfoList.length > 0 ? (
            companyInfoList.map(item => (
              <button
                key={item.id}
                onClick={() => handleSupplierSelect(item)}
                className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
              >
                <div className="font-medium text-gray-900">{item.companyName}</div>
                {item.bizNo && <div className="text-sm text-gray-500">사업자번호: {item.bizNo}</div>}
              </button>
            ))
          ) : (
            <div className="text-center text-gray-500 py-4">등록된 회사 정보가 없습니다.</div>
          )}
        </div>
      </ModalLayout>

      {/* PDF 미리보기 모달 */}
      <ModalLayout
        isOpen={previewOpen}
        onClose={handlePreviewClose}
        title="거래명세표 미리보기"
        maxWidth="max-w-[90vw]"
        maxHeight="max-h-[95vh]"
      >
        <div className="flex flex-col h-full">
          {/* 버튼 영역 */}
          <div className="flex justify-end gap-2 mb-2">
            <Button variant="etc" onClick={handlePdfDownload}>
              다운로드
            </Button>
            <Button variant="etc" onClick={handlePdfPrint}>
              인쇄
            </Button>
          </div>
          {/* PDF 미리보기 */}
          {pdfBlobUrl && (
            <iframe
              src={pdfBlobUrl}
              className="w-full border border-gray-300 rounded"
              style={{ height: "80vh" }}
              title="거래명세표 미리보기"
            />
          )}
        </div>
      </ModalLayout>
    </div>
  );
}
